import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Eye, RefreshCw, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { WeeklyClosingPreview, XlsxButton, inrC, fmtPeriodC } from "@/components/admin/WeeklyClosingPreview";
import { LockCell, EngineRunLocks, MarkPaidButton, LockStepButton } from "@/components/admin/ClosingLocks";
import { useAuth } from "@/lib/auth";

/** Weekly Closing (Admin + Super Admin): Preview → Confirm Weekly Closing → Mark paid → Lock, one step button in the same place. */
export default function AdminClosingCard() {
  const [period, setPeriod] = useState("");
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [top, setTop] = useState(null);        // { period, cap_rs, top: [...] }
  const [loadingTop, setLoadingTop] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const openWeek = history.find((c) => c.status !== "paid" || !c.locked);

  const loadHistory = () => api.get("/admin/weekly-closing/history").then((r) => {
    setHistory(r.data.closings || []);
    setPeriod(r.data.suggested_period_label || "");
  }).catch(() => {});
  const loadTop = () => {
    setLoadingTop(true);
    api.get("/admin/closing/top-earners-preview?limit=10")
      .then((r) => setTop(r.data))
      .catch(() => setTop({ top: [], cap_rs: 250000, total_earners: 0, capped_count: 0 }))
      .finally(() => setLoadingTop(false));
  };

  useEffect(() => { loadHistory(); loadTop(); }, []);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/admin/weekly-closing/preview", { params: { period_label: period.trim() || undefined } });
      setPreview(data);
      setPeriod(data.period_label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const onConfirmed = (data) => {
    toast.success(`Weekly closing finalized — ${data.inserted_rows} members · net ${inrC(data.total_net_rs)} · payment pending`);
    setPreview(null);
    loadHistory();
    loadTop();
  };

  return (
    <>
      <section data-testid="admin-closing-card" className="bg-white border border-border rounded-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 grid place-items-center bg-royal/10 border border-royal/20 rounded-sm">
              <Calendar size={15} className="text-royal" />
            </div>
            <div>
              <h3 className="font-serif text-lg text-navy">Weekly Closing</h3>
              <p className="text-[11px] text-navy/70 -mt-0.5">
                Saturday → Saturday week · Preview → Confirm → Mark paid → Lock
              </p>
            </div>
          </div>
          {openWeek ? <NextStep row={openWeek} isSuperAdmin={isSuperAdmin} onChanged={loadHistory} /> : (
          <div className="flex gap-2 flex-wrap items-end">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-navy/70">Closing week</span>
              <input
                value={period}
                onChange={(e) => { setPeriod(e.target.value); setPreview(null); }}
                placeholder="10_oct_to_17_oct_2026"
                data-testid="closing-period-input"
                className="block mt-0.5 w-56 border border-border rounded-sm px-3 py-2 text-xs font-mono focus:border-royal outline-none"
              />
            </label>
            <button
              type="button"
              onClick={doPreview}
              disabled={previewing}
              data-testid="closing-preview-btn"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-4 py-2 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50 transition-colors"
            >
              {previewing ? <RefreshCw size={12} className="animate-spin" /> : <Eye size={12} />}
              {previewing ? "Calculating…" : "Preview"}
            </button>
          </div>
          )}
        </div>
        <ClosingSteps openWeek={openWeek} previewOpen={!!preview} />

        {preview ? <WeeklyClosingPreview preview={preview} onClose={() => setPreview(null)} onConfirmed={onConfirmed} /> : null}

        <FinalizedClosings rows={history} canLock={isSuperAdmin} onChanged={loadHistory} />
        <EngineRunLocks />

        {/* Top 10 IBAs approaching the weekly cap — read-only projection */}
        <TopEarnersSection top={top} loading={loadingTop} onRefresh={loadTop} />
      </section>
    </>
  );
}

const STEPS = ["Preview", "Confirm", "Mark paid", "Lock"];

/** Step bar: ticks on the steps done for the current week. */
function ClosingSteps({ openWeek, previewOpen }) {
  const done = !openWeek ? (previewOpen ? 1 : 0) : openWeek.status !== "paid" ? 2 : 3;
  return (
    <div className="px-5 py-2 border-b border-border bg-slate-50 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="closing-steps">
      {STEPS.map((s, i) => (
        <span key={s} data-testid={`closing-step-${i + 1}`}
          className={`inline-flex items-center gap-1 ${i < done ? "text-emerald-700 font-semibold" : i === done ? "text-navy font-bold" : "text-navy/50"}`}>
          {i < done ? <CheckCircle2 size={11} /> : <span className="w-4 h-4 grid place-items-center rounded-full border border-current text-[9px]">{i + 1}</span>}
          {s}{i < STEPS.length - 1 ? <span className="text-navy/30 ml-2">→</span> : null}
        </span>
      ))}
    </div>
  );
}

/** Header action while a week is not yet paid + locked: only the next step shows. */
function NextStep({ row, isSuperAdmin, onChanged }) {
  const paid = row.status === "paid";
  return (
    <div className="flex gap-2 flex-wrap items-end" data-testid="closing-next-step">
      <div>
        <span className="text-[10px] uppercase tracking-widest text-navy/70">Closing week</span>
        <div className="mt-0.5 min-w-56 whitespace-nowrap border border-border rounded-sm px-3 py-2 text-xs font-mono bg-slate-50 text-navy" data-testid="closing-step-week">
          {row.period_label} · {inrC(row.total_net_rs)}
        </div>
      </div>
      {isSuperAdmin ? (paid ? <LockStepButton row={row} onChanged={onChanged} /> : <MarkPaidButton row={row} onChanged={onChanged} big />) : (
        <span className="text-[11px] text-amber-800 italic pb-2" data-testid="closing-step-waiting">Waiting for Super Admin to {paid ? "lock" : "mark paid"}</span>
      )}
    </div>
  );
}

function FinalizedClosings({ rows, canLock, onChanged }) {
  return (
    <div className="overflow-x-auto" data-testid="closing-history">
      <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-widest text-navy/70 font-bold">Finalized closings</div>
      {rows.length === 0 ? (
        <div className="px-5 pb-4 text-xs text-navy/70 italic" data-testid="closing-history-empty">No closing recorded</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-navy/80">
            <tr>
              {["Week", "Members", "Gross", "Net", "Status", "Lock", "Finalized at"].map((h) => (
                <th key={h} className="text-left px-5 py-2 font-semibold uppercase tracking-wider text-[10px]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-navy">
            {rows.map((c) => (
              <tr key={c.period_label} data-testid={`closing-history-row-${c.period_label}`} className="border-t border-border/60 hover:bg-slate-50 transition-colors">
                <td className="px-5 py-2 whitespace-nowrap font-medium">
                  <div className="flex items-center gap-2">
                    {fmtPeriodC(c.period_label)}
                    <XlsxButton label={c.period_label} testid={`closing-xlsx-${c.period_label}`} compact />
                  </div>
                </td>
                <td className="px-5 py-2 tabular-nums">{c.members}</td>
                <td className="px-5 py-2 tabular-nums">{inrC(c.total_gross_rs)}</td>
                <td className="px-5 py-2 tabular-nums font-bold text-emerald-700">{inrC(c.total_net_rs)}</td>
                <td className="px-5 py-2"><HistoryPill status={c.status} /></td>
                <td className="px-5 py-2"><LockCell row={c} canEdit={canLock} onChanged={onChanged} /></td>
                <td className="px-5 py-2 whitespace-nowrap text-navy/80">{formatDT(c.closed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function HistoryPill({ status }) {
  const paid = status === "paid";
  const Icon = paid ? CheckCircle2 : Clock;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border whitespace-nowrap ${paid ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-300"}`} data-testid="closing-history-status">
      <Icon size={10} /> {paid ? "Paid" : "Closing finalized · Payment pending"}
    </span>
  );
}

function formatDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
}

/** Top-10 IBAs by projected pair-matching payout this ISO week — helps admin
 *  spot who is about to hit the ₹2,50,000 cap BEFORE they hit Run Closing.
 *  Rendered inline inside AdminClosingCard so both live next to each other.
 */
function TopEarnersSection({ top, loading, onRefresh }) {
  const cap = Number(top?.cap_rs || 250000);
  const period = top?.period || "—";
  const rows = top?.top || [];
  const cappedCount = top?.capped_count || 0;
  const totalEarners = top?.total_earners || 0;
  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  return (
    <div data-testid="closing-top-earners" className="border-t border-border">
      <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap bg-cream/20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-royal font-bold">Approaching cap · this week</div>
          <div className="text-xs text-navy/60 mt-0.5">
            Top 10 IBAs by projected pair income · <b className="text-navy/80">{period}</b>
            {totalEarners > 0 && <> · <span className="text-olive">{totalEarners} earning</span></>}
            {cappedCount > 0 && <> · <span className="text-rose font-bold">{cappedCount} at cap</span></>}
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          data-testid="closing-top-refresh"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-navy/20 text-navy hover:bg-navy/5 rounded-sm disabled:opacity-50"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        {loading && rows.length === 0 ? (
          <div className="p-6 text-xs text-navy/50 italic flex items-center gap-2"><RefreshCw size={12} className="animate-spin" /> Loading projection…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-xs text-navy/50 italic">No IBAs have matched SP this week yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-navy/5 text-navy/60">
              <tr>
                {["#", "IBA", "L : R SP", "Matched SP", "Already ₹", "Projected ₹", "Total ₹", "Cap"].map((h) => (
                  <th key={h} className="text-left px-4 py-2 font-medium uppercase tracking-wider text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="text-navy">
              {rows.map((r, i) => {
                const pct = Math.min(100, Math.round(100 * (r.total_week_rs || 0) / cap));
                const barCls = r.capped ? "bg-rose" : pct >= 80 ? "bg-amber-500" : "bg-olive";
                return (
                  <tr key={r.user_code} data-testid={`top-earner-${r.user_code}`} className="border-t border-border/60 hover:bg-cream/20">
                    <td className="px-4 py-2 text-navy/50 font-mono">{i + 1}</td>
                    <td className="px-4 py-2">
                      <Link to={`/admin/members/${r.user_code}`} className="font-mono font-bold text-royal hover:underline">{r.user_code}</Link>
                      <div className="text-navy/60 text-[10px] truncate max-w-[220px]">{r.name}</div>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-navy/70">{r.left_sp} : {r.right_sp}</td>
                    <td className="px-4 py-2 tabular-nums font-medium">{r.matched_sp}</td>
                    <td className="px-4 py-2 tabular-nums text-navy/70">{inr(r.already_paid_rs)}</td>
                    <td className="px-4 py-2 tabular-nums text-olive font-semibold">{inr(r.projected_pair_rs)}</td>
                    <td className="px-4 py-2 tabular-nums">
                      <div className="font-bold">{inr(r.total_week_rs)}</div>
                      <div className="mt-1 h-1 w-24 bg-navy/10 rounded-full overflow-hidden">
                        <div className={`h-full ${barCls} rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {r.capped ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-rose/40 bg-rose/10 text-rose font-bold">
                          <AlertTriangle size={9} /> AT CAP · {r.carry_over_sp} SP carry
                        </span>
                      ) : pct >= 80 ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-amber-400/50 bg-amber-50 text-amber-800 font-bold">
                          Near · {pct}%
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-olive/30 bg-olive/5 text-olive">
                          {pct}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
